import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'
import { THEMES, THEME_STORAGE_KEY, type Theme } from '../lib/themes'

interface ThemeContextType {
  theme: Theme
  setTheme: (theme: Theme) => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

function getInitialTheme(): Theme {
  const stored = localStorage.getItem(THEME_STORAGE_KEY)
  if (stored) {
    const found = THEMES.find(t => t.id === stored)
    if (found) return found
  }
  return THEMES[0] // default
}

function applyThemeClass(theme: Theme) {
  const html = document.documentElement
  // Remove all theme classes
  for (const t of THEMES) {
    if (t.cssClass) html.classList.remove(t.cssClass)
  }
  // Add the new one
  if (theme.cssClass) html.classList.add(theme.cssClass)

  // Update browser title bar color
  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) meta.setAttribute('content', theme.themeColor)
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(getInitialTheme)

  // Apply on mount (in case inline script didn't run or theme changed)
  useEffect(() => {
    applyThemeClass(theme)
  }, [])

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme)
    localStorage.setItem(THEME_STORAGE_KEY, newTheme.id)
    applyThemeClass(newTheme)
  }

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
